// Edicao de uma publicacao recorrente: texto, imagem, link, destinos e regra.

import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLinks } from "@/lib/hooks/useLinks";
import { useSaveRecurringPost, type RecurringPost } from "@/lib/hooks/useRecurringPosts";
import { defaultDraft, draftEnd, draftFromRule, draftStart, draftToRule, type RecurrenceDraft } from "@/lib/recurrence-draft";
import { DestinationPicker, fromDeliveries, toDeliveries, type DestinationOption } from "./DestinationPicker";
import { RecurrenceEditor } from "./RecurrenceEditor";

interface RecurringEditFormProps {
  readonly post: RecurringPost;
  readonly onDone: (message: string) => void;
  readonly onCancel: () => void;
}

export function RecurringEditForm({ post, onDone, onCancel }: RecurringEditFormProps): JSX.Element {
  const { data: links = [] } = useLinks();
  const save = useSaveRecurringPost();
  const [bodyText, setBodyText] = useState(post.bodyText);
  const [imageUrl, setImageUrl] = useState(post.imageUrl ?? "");
  const [linkId, setLinkId] = useState(post.linkId ?? "");
  const [selected, setSelected] = useState<Record<string, DestinationOption>>(() => fromDeliveries(post.deliveries));
  const [draft, setDraft] = useState<RecurrenceDraft>(() => (post.rule ? draftFromRule(post.rule, post.startsAt, post.endsAt) : defaultDraft()));
  const [error, setError] = useState<string | null>(null);

  const rule = draftToRule(draft);
  const startsAt = draftStart(draft);
  const canSave = bodyText.trim().length > 0 && Object.keys(selected).length > 0 && rule !== null && startsAt !== null && !save.isPending;

  async function handleSave(): Promise<void> {
    if (!rule || !startsAt) return;
    try {
      await save.mutateAsync({
        id: post.id,
        bodyText: bodyText.trim(),
        imageUrl: imageUrl.trim() || null,
        linkId: linkId || null,
        deliveries: toDeliveries(selected),
        rule,
        startsAt,
        endsAt: draftEnd(draft),
      });
      onDone("Publicação recorrente atualizada.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar.");
    }
  }

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div className="space-y-2">
        <Label htmlFor={`rec-body-${post.id}`} variant="field">Texto</Label>
        <textarea
          id={`rec-body-${post.id}`}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          rows={5}
          maxLength={2300}
          className="flex w-full resize-y rounded-md border bg-[hsl(var(--input))] px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`rec-image-${post.id}`} variant="field">URL da imagem (opcional)</Label>
          <Input id={`rec-image-${post.id}`} type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`rec-link-${post.id}`} variant="field">Link de afiliado (opcional)</Label>
          <select
            id={`rec-link-${post.id}`}
            value={linkId}
            onChange={(e) => setLinkId(e.target.value)}
            className="h-9 w-full rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-3 text-sm"
          >
            <option value="">Sem link</option>
            {links.map((link) => (
              <option key={link.id} value={link.id}>
                {link.marketplace_code} - {link.title ?? link.destination_url}
              </option>
            ))}
          </select>
        </div>
      </div>
      <DestinationPicker selected={selected} onChange={setSelected} />
      <RecurrenceEditor draft={draft} onChange={setDraft} />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="button" disabled={!canSave} onClick={() => void handleSave()}>
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Salvar alterações
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
